import { useReducer, useRef } from 'react';

const initialState = {
  // Modals
  showRatingModal: false,
  showTeacherReport: false,

  // Slide navigation
  classSlideIndex: 0,

  // Interactive element state (used by both teacher and student)
  // Note: Named "student" for historical reasons, but teachers also use these
  // to input answers. The sync mechanism routes data correctly based on user role.
  studentElementPositions: {},
  studentInputValues: {},

  // Teacher tools
  laserActive: false,
  highlighterActive: false,
  magnifierActive: false,
  magnifierPosition: { x: -1, y: -1 },
  pointerActive: false,
  pointerPosition: { x: -100, y: -100 },

  // Presentation sharing
  presentationShared: false,
};

function virtualClassroomReducer(state, action) {
  switch (action.type) {
    // Modals
    case 'SET_SHOW_RATING_MODAL':
      return { ...state, showRatingModal: action.payload };
    case 'SET_SHOW_TEACHER_REPORT':
      return { ...state, showTeacherReport: action.payload };

    // Slide navigation
    case 'SET_CLASS_SLIDE_INDEX':
      return { ...state, classSlideIndex: action.payload };

    // Student interaction
    case 'SET_STUDENT_ELEMENT_POSITIONS':
      return {
        ...state,
        studentElementPositions: typeof action.payload === 'function'
          ? action.payload(state.studentElementPositions)
          : action.payload,
      };
    case 'SET_STUDENT_INPUT_VALUES':
      return {
        ...state,
        studentInputValues: typeof action.payload === 'function'
          ? action.payload(state.studentInputValues)
          : action.payload,
      };

    // Teacher tools
    case 'SET_LASER_ACTIVE':
      return { ...state, laserActive: action.payload };
    case 'SET_HIGHLIGHTER_ACTIVE':
      return { ...state, highlighterActive: action.payload };
    case 'SET_MAGNIFIER_ACTIVE':
      return { ...state, magnifierActive: action.payload };
    case 'SET_MAGNIFIER_POSITION':
      return { ...state, magnifierPosition: action.payload };
    case 'SET_POINTER_ACTIVE':
      return { ...state, pointerActive: action.payload };
    case 'SET_POINTER_POSITION':
      return { ...state, pointerPosition: action.payload };

    // Presentation sharing
    case 'SET_PRESENTATION_SHARED':
      return { ...state, presentationShared: action.payload };

    // Compound actions
    case 'RESET_SLIDE':
      return {
        ...state,
        studentElementPositions: {},
        studentInputValues: {},
      };

    default:
      return state;
  }
}

export function useVirtualClassroom() {
  const [state, dispatch] = useReducer(virtualClassroomReducer, initialState);

  const boardRef = useRef(null);

  const setters = {
    setShowRatingModal: (v) => dispatch({ type: 'SET_SHOW_RATING_MODAL', payload: v }),
    setShowTeacherReport: (v) => dispatch({ type: 'SET_SHOW_TEACHER_REPORT', payload: v }),
    setClassSlideIndex: (v) => dispatch({ type: 'SET_CLASS_SLIDE_INDEX', payload: v }),
    setStudentElementPositions: (v) => dispatch({ type: 'SET_STUDENT_ELEMENT_POSITIONS', payload: v }),
    setStudentInputValues: (v) => dispatch({ type: 'SET_STUDENT_INPUT_VALUES', payload: v }),
    setLaserActive: (v) => dispatch({ type: 'SET_LASER_ACTIVE', payload: v }),
    setHighlighterActive: (v) => dispatch({ type: 'SET_HIGHLIGHTER_ACTIVE', payload: v }),
    setMagnifierActive: (v) => dispatch({ type: 'SET_MAGNIFIER_ACTIVE', payload: v }),
    setMagnifierPosition: (v) => dispatch({ type: 'SET_MAGNIFIER_POSITION', payload: v }),
    setPointerActive: (v) => dispatch({ type: 'SET_POINTER_ACTIVE', payload: v }),
    setPointerPosition: (v) => dispatch({ type: 'SET_POINTER_POSITION', payload: v }),
    setPresentationShared: (v) => dispatch({ type: 'SET_PRESENTATION_SHARED', payload: v }),
    resetSlide: () => dispatch({ type: 'RESET_SLIDE' }),
  };

  return {
    ...state,
    ...setters,
    dispatch,
    refs: { boardRef },
  };
}
